import { useState, useEffect } from "react";
import { ENDPOINTS } from '@/config/endpoints';

const HSNPOPUP = ({ setOpenHSNPopup, onHSNSelect }) => {
  const [currentCategories, setCurrentCategories] = useState([]);
  const [breadcrumb, setBreadcrumb] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [payload, setPayload] = useState({}); // Payload for API calls

  const handleHSNSelect = (selectedHSN) => {
    // Close popup
    setOpenHSNPopup(false);
    // Pass the selected HSN back to the parent component
    onHSNSelect(selectedHSN);
  };

  // Fetch initial list of HS Code Groups
  useEffect(() => {
    const fetchhsnCodeGroups = async () => {
      try {
        const response = await fetch(ENDPOINTS.HSN_CODE_GROUPS);
        const data = await response.json();
        const formattedOptions = data.data.map((item) => ({
          hsncode: item.hs_code_group,
          name: item.description,
        }));
        setCurrentCategories(formattedOptions);
      } catch (error) {
        console.error("Error fetching HS Code Groups:", error);
      }
    };
    fetchhsnCodeGroups();
  }, []);

  // Fetch data dynamically based on payload
  const fetchQuotesData = async (newPayload) => {
    console.log("Fetching quotes data with payload:", newPayload);

    try {
        const response = await fetch(ENDPOINTS.HSN_CODE_GROUP_DETAILS,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(newPayload),
        }
      );
      const result = await response.json();

      if (result.data) {
        const formattedOptions = result.data.map((item) => ({
          hsncode: item.hs_code_level_1 || item.hs_code_level_2 || item.hs_code_level_3 || item.hs_code_level_4,
          name: item.description,
        }));
        setCurrentCategories(formattedOptions);
      }
    } catch (error) {
      console.error("Error during API call:", error);
    }
  };

  const handleSelectCategory = (category) => {
    // console.log("Selected category:", category);

    // Update payload dynamically based on breadcrumb length
    const newPayload = { ...payload };

    if (breadcrumb.length === 0) {
      newPayload.group_commodity = category.hsncode;
    } else if (breadcrumb.length === 1) {
      newPayload.commodity_no = category.hsncode;
    } else if (breadcrumb.length === 2) {
      newPayload.sub_commodity_no = category.hsncode;
    } else if (breadcrumb.length === 3) {
      newPayload.hs_code = category.hsncode;
    }

    setPayload(newPayload);
    setBreadcrumb((prev) => [...prev, category]);

    // Fetch next level if not the last
    if (breadcrumb.length < 3) {
      fetchQuotesData(newPayload);
    } else {
      // At the last level, show the selected item
      setSelectedItem(category);
      setCurrentCategories([]); // Clear the list
    }
  };

  const handleBreadcrumbClick = (index) => {
    const newBreadcrumb = breadcrumb.slice(0, index + 1);
    setBreadcrumb(newBreadcrumb);

    // Adjust payload back to the selected level
    const newPayload = { group_commodity: breadcrumb[0]?.hsncode };
    if (index >= 1) newPayload.commodity_no = breadcrumb[1]?.hsncode;
    if (index >= 2) newPayload.sub_commodity_no = breadcrumb[2]?.hsncode;

    setPayload(newPayload);

    // Fetch data for the clicked breadcrumb level
    fetchQuotesData(newPayload);
    setSelectedItem(null);
  };

  const handleConfirm = () => {
    // console.log("Confirmed selection:", selectedItem);
    handleHSNSelect(selectedItem)
    // Pass the selected item to the parent code if needed
    setOpenHSNPopup(false);
  };

  const handleCancel = () => {
    setBreadcrumb([]);
    setCurrentCategories([]);
    setSelectedItem(null);
    setOpenHSNPopup(false);
  };

  return (
    <div className="fixed z-[1060] top-0 left-0 inset-0 w-full h-full">
      <div className="flex items-center sm:items-center justify-center min-h-full p-4 text-center sm:p-0">
        <div className="relative bg-white rounded-lg text-left max-h-[80vh] sm:max-h-[90vh] overflow-y-auto shadow-xl transform transition-all sm:my-8 sm:max-w-lg min-w-[800px] sm:min-w-0 w-[800px] sm:w-[95%] p-3">
          <h2 className="text-lg text-secondary font-bold"> HS Codes </h2>

          {/* Breadcrumb */}
          <div className="text-sm text-blue-600 space-x-1 mb-2 leading-[26px] mt-4">
            <span
              className={`cursor-pointer hover:underline ${breadcrumb.length === 0 ? 'text-black pointer-events-none font-normal' : 'text-brand font-medium'}`}
              onClick={() => {
                setBreadcrumb([]);
                setCurrentCategories([]);
                setSelectedItem(null);
              }}
            >
              Main Category
            </span>
            {breadcrumb.map((crumb, index) => (
              <span key={index} className="space-x-1">
                <span>»</span>
                <span
                  className={`cursor-pointer hover:underline ${index === breadcrumb.length - 1 ? 'text-black pointer-events-none font-normal' : 'text-brand font-medium'}`}
                  onClick={() => handleBreadcrumbClick(index)}
                >
                  {crumb.name}
                </span>
              </span>
            ))}
          </div>

          {/* Category/Subcategory List */}
          {currentCategories.length > 0 ? (
            <ul>
              {currentCategories.map((category) => (
                <li
                  key={category.hsncode}
                  className="flex justify-between items-start cursor-pointer hover:bg-[#f3f3f3] p-1 rounded-md"
                  onClick={() => handleSelectCategory(category)}
                >
                  <span className="pr-5">{category.name}</span>
                  <span className="text-gray-500">{category.hsncode}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p>No categories available.</p>
          )}

          {/* Selected Item Display */}
          {selectedItem && (
            <div className="mt-4 pt-2 border-t">
              <h3 className="font-bold text-base">{selectedItem.name}</h3>
              <p className="text-gray-700">
                Code: <span className="font-mono">{selectedItem.hsncode}</span>
              </p>
            </div>
          )}

          {/* Buttons */}
          <div className="flex justify-end mt-2 space-x-3">
            <button
              className="bg-white rounded-md border border-brand text-brand py-[5px] px-3 shadow-md "
              onClick={handleCancel}
            >
              Cancel
            </button>
            {selectedItem && (
              <button
                className="bg-gradient-to-r from-[#022B5C] to-[#0363C4] inline-block text-white text-base rounded-md border border-brand py-[5px] px-3 shadow-md transition-all duration-300 hover:from-[#0363C4] hover:to-[#022B5C]"
                onClick={handleConfirm}
              >
                Confirm
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HSNPOPUP;